/**
 * DS-3563. `client`'s response interceptor (`./index.ts`) returns
 * `new Promise(() => {})` for a 403 `password_change_required` -- a completed
 * response -- so that promise never settles and neither a timeout, a catch,
 * nor a finally would ever run. Any caller that gates UI on settling (a
 * spinner, a disabled state) is stuck forever if that interceptor branch fires
 * mid-request. Rejecting after a generous window is deliberate: the caller
 * decides how to degrade, and an unsettled promise gives it nothing to decide
 * with.
 *
 * Phase 13 review, finding 5 (DS-4034). Shared rather than duplicated: this
 * started as a `findingsApi`-only helper (`getTicketsAccessStatus`,
 * `startTicketsTrial`); `mobileUsersApi.exportMobileUsers` hits the exact same
 * hazard and had no guard at all, so a caller stuck on that request had no way
 * to recover short of a page reload.
 */
export const DEFAULT_SETTLE_WATCHDOG_MS = 15_000;

export function withSettleWatchdog<T>(
  request: Promise<T>,
  message = 'Request never settled',
  ms: number = DEFAULT_SETTLE_WATCHDOG_MS,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const watchdog = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
    // Node-only (absent in the browser): keeps this timer from holding a
    // test's process open for the full window when the real request settles
    // first and nobody ever needed the rejection. No effect on the timeout
    // itself, in either environment.
    (timer as unknown as { unref?: () => void }).unref?.();
  });
  // Cleared on every settle, including the watchdog's own rejection: once one
  // side of the race resolves there is nothing left for the timer to guard.
  return Promise.race([request, watchdog]).finally(() => clearTimeout(timer));
}
